<template>
  <div class="not-found" :style="brandColors">
    <router-link to="/" class="not-found-logo-link" :aria-label="`${brand.name} home`">
      <brand-logo class="not-found-logo" />
    </router-link>

    <div class="not-found-card">
      <div class="not-found-canvas">
        <svg class="not-found-flow" viewBox="0 20 560 136" aria-hidden="true">
          <circle class="flow-node" cx="30" cy="80" r="16" />
          <path class="flow-line" d="M46 80 H80" />
          <path class="flow-arrowhead" d="M88 80 l-9 -5 v10 z" />
          <rect class="flow-event" x="90" y="48" width="128" height="64" rx="6" />
          <text class="flow-event-label" x="154" y="85">Link opened</text>
          <path class="flow-line" d="M218 80 H250" />
          <path class="flow-arrowhead" d="M258 80 l-9 -5 v10 z" />
          <path class="flow-node" d="M290 48 L322 80 L290 112 L258 80 Z" />
          <text class="flow-gateway-label" x="290" y="89">?</text>
          <path class="flow-search" d="M322 80 H392" />
          <path class="flow-search-arrowhead" d="M400 80 l-9 -5 v10 z" />
          <ellipse class="flow-ghost-shadow" cx="474" cy="146" rx="52" ry="5" />
          <g class="flow-ghost">
            <rect class="flow-ghost-card" x="404" y="36" width="140" height="88" rx="8" />
            <text class="flow-ghost-label" x="474" y="93">404</text>
          </g>
        </svg>
      </div>

      <div class="not-found-body">
        <h1 class="not-found-title">Page not found</h1>
        <p class="not-found-text">The page may not exist, or you may not have access to it.</p>

        <template v-if="isUserLoggedIn">
          <p class="not-found-user">Logged in as <strong>{{ userEmail }}</strong></p>
          <div class="not-found-actions">
            <v-btn to="/dashboard" color="primary" variant="flat" size="large" rounded="lg">
              Go to dashboard
            </v-btn>
            <v-btn variant="outlined" size="large" rounded="lg" data-cy="not-found-logout" @click="logout">
              Log out
            </v-btn>
          </div>
        </template>
        <div v-else class="not-found-actions">
          <v-btn :to="loginLocation" color="primary" variant="flat" size="large" rounded="lg" data-cy="not-found-login">
            Go to login
          </v-btn>
        </div>

        <v-btn
          v-if="canGoBack"
          class="not-found-back"
          variant="text"
          prepend-icon="mdi-arrow-left"
          @click="router.back()"
        >
          Go back
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { authHelpers } from '@/api/authHelpers'
import { useBrand } from '@/composables/useBrand'
import { loginRoute } from '@/utils/loginRedirect'
import BrandLogo from '@/components/BrandLogo.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const brand = useBrand()

const isUserLoggedIn = computed(() => store.state.isUserLoggedIn)

// the user record loads after the page, so fall back to the email from login
const userEmail = computed(() => store.state.user.user?.email || store.state.userInfo)

const loginLocation = computed(() => loginRoute(route.fullPath))

const canGoBack = Boolean(window.history.state?.back)

const brandColors = {
  '--not-found-brand': brand.colors.primary,
  '--not-found-brand-dark': brand.colors.primaryDark
}

const logout = () => {
  authHelpers.logoutAndClearStore()
  router.push(loginLocation.value)
}
</script>

<style scoped>
.not-found {
  --not-found-accent: var(--not-found-brand-dark);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  min-height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
  padding: 48px 0;
}

[data-theme="dark"] .not-found {
  --not-found-accent: var(--not-found-brand);
}

.not-found-logo {
  display: block;
  width: 120px;
}

.not-found-card {
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  border-radius: 16px;
  background: var(--light-1);
  border: 1px solid var(--light-3);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.not-found-canvas {
  padding: 20px 24px;
  border-bottom: 1px solid var(--light-3);
  background-color: var(--surface-secondary);
  background-image:
    radial-gradient(circle at 82% 50%, color-mix(in srgb, var(--not-found-accent) 16%, transparent), transparent 45%),
    radial-gradient(circle, var(--light-4) 1px, transparent 1.5px);
  background-size: auto, 18px 18px;
  background-repeat: no-repeat, repeat;
}

.not-found-flow {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.flow-node {
  fill: var(--surface-elevated);
  stroke: var(--diagram-stroke);
  stroke-width: 2;
}

.flow-line {
  stroke: var(--diagram-stroke);
  stroke-width: 2;
}

.flow-arrowhead {
  fill: var(--diagram-stroke);
}

.flow-event {
  fill: var(--event-orange);
  stroke: var(--event-stroke-orange);
  stroke-width: 1.5;
}

.flow-event-label {
  fill: #1F2833;
  font-size: 14px;
  font-weight: 600;
  text-anchor: middle;
}

.flow-gateway-label {
  fill: var(--diagram-stroke);
  font-size: 26px;
  font-weight: 700;
  text-anchor: middle;
}

.flow-search {
  stroke: var(--not-found-accent);
  stroke-width: 2;
  stroke-dasharray: 6 6;
  animation: flow-search 1.25s linear 4;
}

.flow-search-arrowhead {
  fill: var(--not-found-accent);
}

.flow-ghost {
  animation: flow-ghost-float 1.25s ease-in-out 4 alternate;
}

.flow-ghost-card {
  fill: color-mix(in srgb, var(--not-found-accent) 10%, var(--light-1));
  stroke: var(--not-found-accent);
  stroke-width: 2;
  stroke-dasharray: 8 6;
}

.flow-ghost-label {
  fill: var(--not-found-accent);
  font-family: Faktum, sans-serif;
  font-size: 38px;
  font-weight: 700;
  text-anchor: middle;
}

.flow-ghost-shadow {
  fill: var(--diagram-stroke);
  opacity: 0.12;
  transform-box: fill-box;
  transform-origin: center;
  animation: flow-ghost-shadow 1.25s ease-in-out 4 alternate;
}

.not-found-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 32px 32px;
  text-align: center;
}

.not-found-title {
  margin: 0 0 12px;
  font-family: Faktum, sans-serif;
  font-size: clamp(30px, 6vw, 44px);
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: -1px;
  color: var(--text-dark);
}

.not-found-text {
  max-width: 440px;
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-light);
}

.not-found-user {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-light);
  overflow-wrap: anywhere;

  strong {
    font-weight: 600;
    color: var(--text-dark);
  }
}

.not-found-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;

  .v-btn {
    min-width: 160px;
  }
}

.not-found-back {
  margin-top: 12px;
  color: var(--text-light);
}

@keyframes flow-search {
  from {
    stroke-dashoffset: 24;
  }

  to {
    stroke-dashoffset: 0;
  }
}

@keyframes flow-ghost-float {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(-8px);
  }
}

@keyframes flow-ghost-shadow {
  from {
    transform: scaleX(1);
    opacity: 0.12;
  }

  to {
    transform: scaleX(0.82);
    opacity: 0.06;
  }
}

@media (prefers-reduced-motion: reduce) {
  .flow-search,
  .flow-ghost,
  .flow-ghost-shadow {
    animation: none;
  }
}

@media (max-width: 520px) {
  .not-found {
    gap: 20px;
    padding: 24px 0;
  }

  .not-found-canvas {
    padding: 12px;
  }

  .not-found-body {
    padding: 24px 20px 28px;
  }

  .not-found-actions {
    flex-direction: column;
    align-self: stretch;

    .v-btn {
      width: 100%;
    }
  }
}
</style>
